// Optional LLM layer for the site assistant.
//
// DISABLED BY DEFAULT. The assistant is fully functional without it — every
// answer it gives today comes from the local knowledge base at zero cost. This
// layer only handles questions the knowledge base can't, and only once MRE has
// bought credits.
//
// To switch on: set REACT_APP_CHAT_LLM=on in the frontend environment. The
// backend also needs a key configured; without one it returns 503 and the
// assistant quietly falls back to the knowledge base.
//
// There is deliberately nowhere here to put an API key. The browser only ever
// talks to our own backend — a key shipped in front-end JavaScript is readable
// by anyone who opens devtools, and spendable by them too.

const DEFAULTS = {
  enabled: false,
  endpoint: "",
  timeoutMs: 20000,
  maxHistory: 8,
  // Spend guards. Enforced client-side as a first line; the backend enforces
  // its own limits independently, because anything here can be bypassed.
  maxMessagesPerSession: 20,
  maxCharsPerMessage: 1500,
};

// Read the switch from the build environment. Any value other than "on"
// (including undefined, the default) leaves the LLM off.
export function llmConfigFromEnv() {
  const on = process.env.REACT_APP_CHAT_LLM === "on";
  // Derived from the backend URL directly rather than importing lib/api, which
  // would drag axios (and the whole app runtime) into anything that touches
  // this module — including the test harness.
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return {
    enabled: on,
    endpoint: process.env.REACT_APP_CHAT_LLM_ENDPOINT || `${base}/api/chat`,
  };
}

export class ChatLLM {
  constructor(config = {}) {
    this.cfg = { ...DEFAULTS, ...config };
    this.spent = 0;
  }

  configure(config = {}) {
    this.cfg = { ...this.cfg, ...config };
  }

  // { ok, reason } — the single place that decides whether we may spend.
  check() {
    if (!this.cfg.enabled) return { ok: false, reason: "disabled" };
    if (!this.cfg.endpoint) return { ok: false, reason: "no-endpoint" };
    if (this.spent >= this.cfg.maxMessagesPerSession) return { ok: false, reason: "session-cap" };
    return { ok: true, reason: "" };
  }

  get isEnabled() {
    return this.check().ok;
  }

  // Ask the backend. `context` is the knowledge-base material the rules engine
  // already found — sent along so the model answers from MRE's facts rather
  // than inventing them.
  async ask(question, history = [], context = []) {
    const gate = this.check();
    if (!gate.ok) throw new Error(`llm-unavailable:${gate.reason}`);

    this.spent += 1;

    const payload = {
      question: String(question || "").slice(0, this.cfg.maxCharsPerMessage),
      history: history.slice(-this.cfg.maxHistory).map((m) => ({
        role: m.role,
        content: String(m.content).slice(0, this.cfg.maxCharsPerMessage),
      })),
      context,
    };

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.cfg.timeoutMs);
    try {
      const res = await fetch(this.cfg.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`llm-http-${res.status}`);
      const data = await res.json();
      const answer = data?.answer;
      if (!answer) throw new Error("llm-empty-response");
      return String(answer);
    } finally {
      clearTimeout(timer);
    }
  }
}

export default ChatLLM;
